/**
 * 模块配置工具函数
 * 用于加载和处理模块配置
 * @author AI Assistant
 * @date 2024-07-08
 */

import type { RouteRecordRaw } from 'vue-router'
import type { ModuleConfig, ModuleRouteItem } from '@/types/module-config'
import type { NavigationItem, ModuleNavigation } from '@/types/navigation'
import { auth } from '@/auth'

/**
 * 加载模块配置
 * @param moduleId 模块ID
 * @returns 模块配置
 */
export async function loadModuleConfig(moduleId: string): Promise<ModuleConfig | null> {
  try {
    // 使用 import.meta.glob 生成的映射进行按需加载，确保生产环境可用
    const modules = import.meta.glob('@/modules/*/config/module.ts')
    const key = `/src/modules/${moduleId}/config/module.ts`
    const loader = modules[key]
    if (!loader) {
      console.error(`Module config not found for ${moduleId}:`, key)
      return null
    }
    const mod: any = await loader()
    return (mod?.default ?? mod) as ModuleConfig
  } catch (error) {
    console.error(`Failed to load module config for ${moduleId}:`, error)
    return null
  }
}

/**
 * 加载所有模块配置
 * @returns 模块配置列表
 */
export async function loadAllModuleConfigs(): Promise<ModuleConfig[]> {
  // 这里可以通过Vite的import.meta.glob动态导入所有模块配置
  // 在实际项目中，可以根据需要调整导入方式
  const moduleFiles = import.meta.glob('@/modules/*/config/module.ts')
  const moduleConfigs: ModuleConfig[] = []

  const importPromises = Object.entries(moduleFiles).map(async ([path, importFn]) => {
    try {
      const module = await importFn()
      const config = module.default as ModuleConfig
      if (config && config.enabled !== false) {
        moduleConfigs.push(config)
      }
    } catch (error) {
      console.error(`Failed to load module config from ${path}:`, error)
    }
  })

  await Promise.all(importPromises)
  return moduleConfigs
}

/**
 * 将模块路由项转换为Vue Router路由记录
 * @param routeItem 模块路由项
 * @param parentPath 父路径
 * @returns Vue Router路由记录
 */
export function convertToRouteRecord(routeItem: ModuleRouteItem, parentPath: string = ''): RouteRecordRaw {
  const fullPath = parentPath ? `${parentPath}/${routeItem.path}`.replace(/\/\/+/g, '/') : routeItem.path

  const route: RouteRecordRaw = {
    path: fullPath,
    name: routeItem.name,
    redirect: routeItem.redirect,
    meta: { ...routeItem.meta },
    // 动态导入组件
    component: routeItem.component ? (() => {
      const layouts = import.meta.glob('/src/layouts/**/*.vue')
      const modulesPages = import.meta.glob('/src/modules/**/*.vue')
      const views = import.meta.glob('/src/views/**/*.vue')
      const resolveByKey = (key: string) => {
        if (layouts[key]) return (layouts[key] as () => Promise<any>)()
        if (modulesPages[key]) return (modulesPages[key] as () => Promise<any>)()
        if (views[key]) return (views[key] as () => Promise<any>)()
        return Promise.reject(new Error(`Component not found: ${key}`))
      }
      const componentPath = routeItem.component
      if (typeof componentPath !== 'string') {
        return Promise.reject(new Error('组件路径必须是字符串类型'))
      }
      let key = ''
      if (componentPath.startsWith('@/')) {
        key = componentPath.replace('@/', '/src/')
      } else if (componentPath.startsWith('../')) {
        let moduleName = 'error'
        if (routeItem.name) {
          const routeToModuleMap: Record<string, string> = { login: 'auth', register: 'auth', 'forgot-password': 'auth' }
          if (routeToModuleMap[routeItem.name]) {
            moduleName = routeToModuleMap[routeItem.name] || 'error'
          } else {
            const nameSegments = routeItem.name.split('-')
            moduleName = nameSegments[0] || 'error'
          }
        } else {
          const pathSegments = fullPath.split('/').filter(Boolean)
          moduleName = pathSegments[0] || 'error'
        }
        const cleanPath = componentPath.replace(/^\.\.\//, '')
        key = `/src/modules/${moduleName}/${cleanPath}`
      } else if (componentPath.startsWith('/')) {
        key = componentPath
      } else if (componentPath.includes('/')) {
        key = `/src/${componentPath}`
      } else {
        key = `/src/modules/${componentPath}`
      }
      if (!key.endsWith('.vue')) key = `${key}.vue`
      return resolveByKey(key)
    }) : undefined
  }

  if (routeItem.children && routeItem.children.length > 0) {
    route.children = routeItem.children.map(child => convertToRouteRecord(child, fullPath))
  }

  return route
}

/**
 * 从模块配置生成路由
 * @param moduleConfigs 模块配置列表
 * @returns 路由列表
 */
export function generateRoutesFromModuleConfigs(moduleConfigs: ModuleConfig[]): RouteRecordRaw[] {
  const routes: RouteRecordRaw[] = []

  for (const config of moduleConfigs) {
    for (const routeItem of config.routes) {
      routes.push(convertToRouteRecord(routeItem))
    }
  }

  return routes
}

/**
 * 从模块配置生成导航配置
 * @param moduleConfigs 模块配置列表
 * @returns 导航配置列表
 */
export function generateNavigationFromModuleConfigs(moduleConfigs: ModuleConfig[]): ModuleNavigation[] {
  return moduleConfigs
    .map(config => config.navigation)
    .filter((nav): nav is ModuleNavigation => {
      // 过滤掉无效的导航配置
      return nav != null && typeof nav === 'object' && Array.isArray(nav.items)
    })
}

/**
 * 从模块配置生成权限列表
 * @param moduleConfigs 模块配置列表
 * @returns 权限列表
 */
export function generatePermissionsFromModuleConfigs(moduleConfigs: ModuleConfig[]) {
  const permissions = []

  for (const config of moduleConfigs) {
    permissions.push(...config.permissions)
  }

  return permissions
}

/**
 * 根据权限过滤路由
 * @param routes 路由列表
 * @param permissions 权限列表
 * @param roles 角色列表
 * @returns 过滤后的路由列表
 */
export function filterRoutesByPermission(
  routes: RouteRecordRaw[],
  permissions: string[] = [],
  roles: string[] = []
): RouteRecordRaw[] {
  const isAdmin = roles.includes('admin') || roles.includes('super_admin')

  return routes.filter(route => {
    // 管理员可以访问所有路由
    if (isAdmin) return true

    // 检查路由权限
    const routePermissions = route.meta?.permissions as string[] | undefined
    const routeRoles = route.meta?.roles as string[] | undefined

    // 如果没有权限要求，则允许访问
    if (!routePermissions && !routeRoles) return true

    // 检查权限
    const hasPermission = !routePermissions || routePermissions.some(p => permissions.includes(p))

    // 检查角色
    const hasRole = !routeRoles || routeRoles.some(r => roles.includes(r))

    // 如果有子路由，递归过滤
    if (route.children) {
      route.children = filterRoutesByPermission(route.children, permissions, roles)
    }

    return hasPermission && hasRole
  })
}

/**
 * 根据权限过滤导航
 * @param navigations 导航配置列表
 * @param permissions 权限列表
 * @param roles 角色列表
 * @returns 过滤后的导航配置列表
 */
export function filterNavigationsByPermission(
  navigations: ModuleNavigation[],
  permissions: string[] = [],
  roles: string[] = []
): ModuleNavigation[] {
  const isAdmin = roles.includes('admin') || roles.includes('super_admin')

  return navigations.map(nav => {
    // 过滤导航项
    const filteredItems = nav.items.filter(item => {
      // 管理员可以访问所有菜单
      if (isAdmin) return true

      // 检查菜单权限
      const itemPermissions = item.permissions

      // 如果没有权限要求，则允许访问
      if (!itemPermissions || itemPermissions.length === 0) return true

      // 检查权限
      return itemPermissions.some(p => permissions.includes(p))
    })

    return {
      ...nav,
      items: filteredItems
    }
  }).filter(nav => nav.items.length > 0) // 过滤掉没有子项的导航
}
